'use client';

import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { AppDispatch } from '@/store/store';
import type Address from '@/models/geographies/Address';
import { Order } from '@/models/orders';

import {
	CHECKOUT_STEP_AUTH,
	CHECKOUT_STEP_SELECT_PAYMENT_DETAILS,
	CHECKOUT_STEP_SHIPPING_ADDRESS,
} from '@/constants/checkout';
import {
	ERROR_PAYMENT_METHOD_NOT_LOADED,
} from '@/constants/errors';
import {
	TX_BILLING_ADDRESS,
	TX_CART_SUMMARY_TITLE,
	TX_CHECKOUT_BILLING_ADDRESS_SAME,
	TX_CHECKOUT_PLACE_ORDER,
	TX_CHECKOUT_PLACING_ORDER,
	TX_CONTACT_US,
} from '@/constants/strings';
import {
	URL_CONTACT_US,
	URL_SUCCESS,
} from '@/constants/urls';

import { getStripeInstance } from '@/lib/stripe-payment';

import {
	isLoggedIn,
} from '@/utils/auth';
import {
	checkoutGetIsStoreCreditApplied,
	checkoutGetSelectedPaymentMethod,
	checkoutGetSelectedShippingMethods,
	getCartGrandTotal,
	getCheckoutStepData,
} from '@/utils/checkout';
import { getCurrencyCode } from '@/utils/currency';
import { formatServerError } from '@/utils/formatting';
import { getUrlParams } from '@/utils/request';

import {
	checkoutPlaceOrder,
	checkoutClearStepData,
} from '@/actions/checkout';
import { paymentsStripeCreatePaymentIntent } from '@/actions/payments';

import CheckoutTotals from '@/components/Cart/totals/CheckoutTotals';
import Checkbox from '@/components/Input/Checkbox';
import LoadingIcon from '@/components/Icons/LoadingIcon';
import CheckoutStepPaymentDetailsBillingAddressInput from '@/components/Checkout/blocks/steps/payment-details/CheckoutStepPaymentDetailsBillingAddressInput';

import '@/components/Settings/_styles/_paymentmethods.scss';


interface StripeCCPaymentInputProps {
	paymentMethod: any;
	config: any;
	setStepData: (data: any) => void;
}

const StripeCCPaymentInput = (props: StripeCCPaymentInputProps): JSX.Element => {

	// Dispatch
	const dispatch = useDispatch<AppDispatch>();

	// Props
	const { paymentMethod, config, setStepData } = props;

	// Local state
	const [stripeLoading, setStripeLoading] = useState(true);
	const [stripeReady, setStripeReady] = useState(false);
	const [inputSameBilling, setInputSameBilling] = useState(true);
	const [submitReqError, setSubmitReqError] = useState<string | null>(null);
	const [submitReqPending, setSubmitReqPending] = useState(false);
	const [saveSignal, setSaveSignal] = useState(0);
	const [validationSignal, setValidationSignal] = useState(0);

	// Refs
	const stripeRef = useRef<any>(null);
	const elementsRef = useRef<any>(null);
	const cardElementRef = useRef<any>(null);
	const cardContainerRef = useRef<HTMLDivElement>(null);

	// Accessing Redux state
	const cart = useSelector((state: any) => state.cart);
	const checkout = useSelector((state: any) => state.checkout);
	const user = useSelector((state: any) => state.user);

	// Internationalization
	const { t } = useTranslation();

	// Router
	const router = useRouter();

	// Derived values
	const selectedPaymentMethod = checkoutGetSelectedPaymentMethod(checkout.stepData);
	const selectedShippingMethods = checkoutGetSelectedShippingMethods(checkout.stepData);

	// Methods
	const getShippingAddress = useCallback(() => {
		const stepData = getCheckoutStepData(CHECKOUT_STEP_SHIPPING_ADDRESS, checkout.stepData);
		if (stepData && stepData.data && stepData.data.shippingAddress) {
			return stepData.data.shippingAddress;
		}
		return null;
	}, [checkout.stepData]);

	const getBillingAddress = useCallback(() => {
		if (inputSameBilling) {
			const stepData = getCheckoutStepData(CHECKOUT_STEP_SHIPPING_ADDRESS, checkout.stepData);
			if (stepData && stepData.data && stepData.data.shippingAddress) {
				return stepData.data.shippingAddress;
			}
		} else {
			const stepData = getCheckoutStepData(CHECKOUT_STEP_SELECT_PAYMENT_DETAILS, checkout.stepData);
			if (stepData && stepData.data && stepData.data.billingAddress) {
				return stepData.data.billingAddress;
			}
		}
		return null;
	}, [inputSameBilling, checkout.stepData]);

	const getOrderEmail = useCallback(() => {
		if (isLoggedIn()) {
			return user.user.email;
		}
		const authStepData = getCheckoutStepData(CHECKOUT_STEP_AUTH, checkout.stepData);
		if (authStepData && authStepData.data && authStepData.data.isGuest) {
			return authStepData.data.guestUser.email;
		}
		return '';
	}, [user, checkout.stepData]);

	// savedBillingAddress: passed by the billing form (its saveComplete callback) when the customer uses a separate
	// billing address. It has also written it to step data, but this callback's closure still holds the step data from
	// before the save, so reading only from there found nothing and the first Place Order did nothing.
	const completeSave = useCallback(async (savedBillingAddress: Address | null = null) => {
		setSubmitReqError(null);
		setSubmitReqPending(true);
		document.body.style.overflow = 'hidden';

		try {
			const billingAddress = savedBillingAddress ?? getBillingAddress();
			if (!billingAddress) {
				setSubmitReqPending(false);
				document.body.style.overflow = 'visible';
				return;
			}

			const grandTotal = getCartGrandTotal(cart.currentCart, checkout.stepData);

			// Step 1: Create PaymentIntent on the backend
			const intentResp = await dispatch(paymentsStripeCreatePaymentIntent(
				selectedPaymentMethod.publicUuid,
				{
					amount: grandTotal,
					currency: getCurrencyCode(),
					cart_uuid: cart.currentCart.publicUuid,
					use_store_credit: checkoutGetIsStoreCreditApplied(checkout.stepData),
				},
			));

			if (!intentResp || !intentResp.client_secret) {
				setSubmitReqError('Payment initialization failed');
				setSubmitReqPending(false);
				document.body.style.overflow = 'visible';
				return;
			}

			// Step 2: Confirm payment with Stripe.js
			const stripe = stripeRef.current;
			const cardElement = cardElementRef.current;

			const billingDetails: any = {
				name: `${billingAddress.firstName} ${billingAddress.lastName}`.trim(),
				address: {
					line1: billingAddress.street1,
					line2: billingAddress.street2 || undefined,
					city: billingAddress.city,
					state: billingAddress.state || billingAddress.region || undefined,
					postal_code: billingAddress.postCode,
					country: billingAddress.country?.key || billingAddress.country || undefined,
				},
			};

			const email = getOrderEmail();
			if (email) {
				billingDetails.email = email;
			}
			if (billingAddress.phone) {
				billingDetails.phone = billingAddress.phone;
			}

			const { error, paymentIntent } = await stripe.confirmCardPayment(
				intentResp.client_secret,
				{
					payment_method: {
						card: cardElement,
						billing_details: billingDetails,
					},
				},
			);

			if (error) {
				setSubmitReqError(error.message || 'Payment confirmation failed');
				setSubmitReqPending(false);
				document.body.style.overflow = 'visible';
				return;
			}

			if (!paymentIntent || paymentIntent.status !== 'succeeded') {
				setSubmitReqError('Payment was not completed');
				setSubmitReqPending(false);
				document.body.style.overflow = 'visible';
				return;
			}

			// Step 3: Place the order with the confirmed PaymentIntent ID
			const shippingAddress = getShippingAddress();
			const shippingMethodUuids = selectedShippingMethods
				.filter((locationMethod: any) => {
					return locationMethod.shippingMethod.publicUuid && locationMethod.location.publicUuid;
				})
				.map((locationMethod: any) => ({
					shipping_method_uuid: locationMethod.shippingMethod.publicUuid,
					location_uuid: locationMethod.location.publicUuid,
				}));

			const orderData = {
				shipping_address_uuid: shippingAddress?.publicUuid,
				shipping_method_uuids: shippingMethodUuids,
				billing_address_uuid: billingAddress.publicUuid,
				payment_method_uuid: selectedPaymentMethod.publicUuid,
				cart_uuid: cart.currentCart.publicUuid,
				payment_record_uuid: null,
				payment_intent_id: paymentIntent.id,
				status: cart.currentCart.hasPreorderItems() ? Order.preorderStatus : Order.initialStatus,
				use_store_credit: checkoutGetIsStoreCreditApplied(checkout.stepData),
			};

			const orderResp = await dispatch(checkoutPlaceOrder(orderData));

			if (!orderResp || !orderResp.public_uuid) {
				setSubmitReqPending(false);
				document.body.style.overflow = 'visible';
				return;
			}

			dispatch(checkoutClearStepData());

			const uuidParams = { order: orderResp.public_uuid };
			router.push(`${URL_SUCCESS}${getUrlParams(uuidParams)}`);

		} catch (err: any) {
			console.error(err);
			setSubmitReqError(formatServerError(err));
			setSubmitReqPending(false);
			document.body.style.overflow = 'visible';
		}
	}, [
		dispatch, router, cart, checkout, user,
		selectedPaymentMethod, selectedShippingMethods,
		getBillingAddress, getShippingAddress, getOrderEmail,
	]);

	const handleSubmit = useCallback((evt?: React.FormEvent) => {
		if (evt) { evt.preventDefault(); }

		if (inputSameBilling) {
			completeSave();
		} else {
			setSaveSignal(Date.now());
		}
	}, [inputSameBilling, completeSave]);

	// Effects

	// Initialize Stripe on mount
	useEffect(() => {
		let mounted = true;

		const initStripe = async () => {
			try {
				// Direct charges require the Stripe.js instance to be scoped to the
				// connected account. Without this, the platform-scoped Stripe.js
				// can't see the PaymentIntent and confirmCardPayment fails with
				// "No such payment_intent".
				const stripeAccountId = paymentMethod?.configData?.stripe_user_id;
				if (!stripeAccountId) {
					console.error('Stripe payment method missing connected account ID in config_data');
					if (mounted) {
						setStripeLoading(false);
						setStripeReady(false);
					}
					return;
				}

				const stripe = await getStripeInstance(stripeAccountId);
				if (!mounted || !stripe) return;

				stripeRef.current = stripe;
				const elements = stripe.elements();
				elementsRef.current = elements;

				const card = elements.create('card', {
					disableLink: true,
					style: {
						base: {
							fontSize: '16px',
							fontFamily: 'inherit',
							color: '#32325d',
							'::placeholder': {
								color: '#aab7c4',
							},
						},
						invalid: {
							color: '#fa755a',
							iconColor: '#fa755a',
						},
					},
				});

				cardElementRef.current = card;
				setStripeLoading(false);
				setStripeReady(true);
			} catch (err) {
				console.error('Failed to initialize Stripe:', err);
				if (mounted) {
					setStripeLoading(false);
					setStripeReady(false);
				}
			}
		};

		initStripe();

		return () => {
			mounted = false;
			document.body.style.overflow = 'visible';
		};
	}, []);

	// Mount card element when container is ready
	useEffect(() => {
		if (!stripeLoading && stripeReady && cardElementRef.current && cardContainerRef.current) {
			cardElementRef.current.mount(cardContainerRef.current);
		}
		return () => {
			if (cardElementRef.current) {
				try {
					cardElementRef.current.unmount();
				} catch {
					// Element may already be unmounted
				}
			}
		};
	}, [stripeLoading, stripeReady]);

	// Prevent navigation during payment
	useEffect(() => {
		const checkNav = (evt: BeforeUnloadEvent) => {
			if (submitReqPending) {
				evt.preventDefault();
			}
		};
		window.addEventListener('beforeunload', checkNav);
		return () => {
			window.removeEventListener('beforeunload', checkNav);
		};
	}, [submitReqPending]);

	// Render
	return (
		<div className={'SquareCCPaymentInput CheckoutStepComponent'}>
			<div className='pmiLiner'>
				{stripeLoading ? (
					<div className='squareLoading'>
						<LoadingIcon iconClass='iconElement' />
					</div>
				) : (
					<>
						{!stripeReady && (
							<div className='squareLoadError'>
								<div className='squareLoadErrorTitle'>{t(ERROR_PAYMENT_METHOD_NOT_LOADED)}</div>
								<div className='squareLoadErrorLinkWrapper'>
									<Link href={URL_CONTACT_US}>{t(TX_CONTACT_US)}</Link>
								</div>
							</div>
						)}
					</>
				)}
				<div className={submitReqError ? 'squareServerError present' : 'squareServerError'}>
					{submitReqError ? t(submitReqError) : ''}
				</div>
				<form
					className={'checkoutStepInputForm'}
					onSubmit={handleSubmit}
					style={{
						display: stripeLoading || !stripeReady ? 'none' : 'block',
					}}>
					<div className={'paymentSection'}>
						<div id='stripeCardContainer' ref={cardContainerRef}></div>
						<div className={'checkoutFormInput sameAsShippingWrapper'}>
							<div className='sameAsShippingInputWrapper'>
								<Checkbox
									name={t(TX_BILLING_ADDRESS)}
									id={'sameAsShippingInput'}
									value={'same-as-shipping'}
									checked={inputSameBilling}
									onChange={() => setInputSameBilling(!inputSameBilling)} />
							</div>
							<label
								htmlFor={'sameAsShippingInput'}
								className='sameAsShippingValueWrapper'>
								{t(TX_CHECKOUT_BILLING_ADDRESS_SAME)}
							</label>
						</div>
					</div>
					{!inputSameBilling && (
						<div className={'billingSection'}>
							<CheckoutStepPaymentDetailsBillingAddressInput
								config={config}
								setStepData={setStepData}
								saveSignal={saveSignal}
								saveComplete={completeSave}
								validationSignal={validationSignal} />
						</div>
					)}
					<div className='checkoutSummaryWrapper'>
						<div className='summaryTitle'>{t(TX_CART_SUMMARY_TITLE)}</div>
						<CheckoutTotals
							cartLoading={false} />
					</div>
					<div className={'checkoutFormInput checkoutFormAction placeOrderAction'}>
						<button
							type='submit'
							className='checkoutFormButton'
							disabled={submitReqPending}>
							{t(TX_CHECKOUT_PLACE_ORDER)}
						</button>
					</div>
				</form>
			</div>
			<div className={`checkoutSubmitScreen ${submitReqPending ? 'active' : ''}`}>
				<div className='submitScreenLiner'>
					<div className='submitIconWrapper'>
						<LoadingIcon />
					</div>
					<div className='submitIconLabel'>{t(TX_CHECKOUT_PLACING_ORDER)}</div>
				</div>
			</div>
		</div>
	);
};

export default StripeCCPaymentInput;
